"use client";

import { Mail } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function RevenueOptimizationFaq() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    value: "item-1",
    question: "What does a revenue audit involve?",
    answer: "Our revenue audit is a comprehensive deep-dive into your current OTA performance, pricing parity, and distribution mix. We analyze your historical data to identify missed opportunities, technical bottlenecks, and areas where your property is losing potential revenue to competitors."
  }, {
    value: "item-2",
    question: "How long does a typical engagement last?",
    answer: "Consulting engagements are tailored to your specific needs. While initial audits can be completed in 2-4 weeks, our ongoing revenue management support is designed as a long-term partnership to ensure sustained growth and market adaptability."
  }, {
    value: "item-3",
    question: "Do I need to change my current tech stack?",
    answer: "Not necessarily. We first evaluate your existing hospitality tech stack for efficiency. If your current tools are hindering performance, we provide objective recommendations for upgrades or integrations that align with your revenue goals."
  }, {
    value: "item-4",
    question: "What is your cancellation policy for consulting?",
    answer: "We operate on a professional service agreement basis. We require a 30-day notice for the termination of ongoing monthly management services to ensure a smooth transition and proper handover of your revenue operations."
  }, {
    value: "item-5",
    question: "Are remote consultations available?",
    answer: "Yes. As a digital-first consultancy, we work with hotel owners globally. We conduct all strategy sessions, audits, and performance reviews via secure video conferencing, ensuring seamless collaboration regardless of your property's location."
  }, {
    value: "item-6",
    question: "Can I hire you for a one-time project?",
    answer: "Absolutely. We offer project-based consulting for specific needs such as OTA listing optimization, tech stack implementation, or seasonal revenue strategy planning. Contact us to discuss your specific requirements."
  }, {
    value: "item-7",
    question: "Which hotel types do you work with?",
    answer: "We specialize in independent hotels, boutique properties, and small-to-medium hotel groups. Our strategies are highly adaptable, focusing on maximizing RevPAR and direct bookings for properties that need a competitive edge."
  }, {
    value: "item-8",
    question: "How soon will I see revenue improvements?",
    answer: "While results vary based on market conditions and property size, many clients see improvements in OTA visibility and conversion rates within the first 30 to 60 days of implementing our data-driven strategies."
  }];
  return <section data-section-id="3156" id="revenueoptimizationfaq" className="bg-background py-20 md:py-[120px]">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className={`lg:col-span-4 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:sticky lg:top-24">
              <p className="font-default text-xs tracking-[0.25em] uppercase text-primary mb-6">
                Frequently Asked
              </p>
              <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] text-foreground mb-6">
                Revenue <span className="italic font-light">Insights.</span>
              </h2>
              <p className="font-default text-base leading-relaxed text-muted-foreground mb-8 max-w-md">
                Common questions from hotel owners and managers. If your specific challenge isn't addressed here, we are ready to discuss it directly.
              </p>
              <Link to="/ContactPage" className="inline-block">
                <Button variant="ghost" className="group h-auto px-0 py-2 font-default text-sm tracking-wide text-foreground hover:bg-transparent hover:text-primary">
                  <Mail className="size-4 stroke-[1.25] mr-2" />
                  <span>Contact our team</span>
                  <span className="ml-1 transition-transform duration-500 ease-out group-hover:translate-x-1">
                    →
                  </span>
                </Button>
              </Link>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-8 transition-all duration-1000 delay-150 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Accordion type="single" collapsible className="w-full border-t border-border">
              {faqs.map((faq, i) => <AccordionItem data-index={i} key={faq.value} value={faq.value} className="border-b border-border last:border-b-0">
                  <AccordionTrigger className="group flex w-full items-center justify-between gap-6 py-8 text-left hover:no-underline [&>svg]:hidden">
                    <span className="font-default font-semibold text-xl md:text-[1.4rem] leading-snug text-foreground transition-colors duration-300 group-hover:text-primary">
                      {faq.question}
                    </span>
                    <span className="relative flex size-8 shrink-0 items-center justify-center text-primary">
                      <span className="absolute h-px w-4 bg-current" />
                      <span className="absolute h-4 w-px bg-current transition-transform duration-500 ease-out group-data-[state=open]:rotate-90 group-data-[state=open]:scale-0" />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pr-12">
                    <p className="font-default text-base leading-[1.7] text-muted-foreground">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}